import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Divider } from '@mui/material';
import {
  PanTool as PanToolIcon,
  Mouse as CursorIcon,
  Add as AddIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as ResetZoomIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  ViewColumn as ChapterBorderIcon,
  AddBox as AddBorderIcon,
} from '@mui/icons-material';
import { useTimeline, TOOL_MODES } from './TimelineProvider';
import useThemeStyles from '../../hooks/useThemeStyles';

// Define button component to match CorkboardHotbar style
const ToolButton = ({ icon, tooltip, isActive, onClick, disabled }) => (
  <Tooltip title={tooltip} placement="top">
    <span>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        sx={{
          backgroundColor: isActive ? 'primary.main' : 'background.paper',
          color: isActive ? 'primary.contrastText' : 'text.primary',
          '&:hover': {
            backgroundColor: isActive ? 'primary.dark' : 'action.hover',
          },
          '&:disabled': {
            backgroundColor: 'action.disabledBackground',
            color: 'action.disabled',
          },
          width: 40,
          height: 40,
        }}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

ToolButton.propTypes = {
  icon: PropTypes.node.isRequired,
  tooltip: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ToolButton.defaultProps = {
  isActive: false,
  disabled: false,
};

const TimelineToolbar = () => {
  const { isDark } = useThemeStyles();
  const {
    toolMode,
    setToolMode,
    viewState,
    setZoom,
    resetZoom,
    showChapterBorders,
    toggleBordersVisibility,
    addBorder,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useTimeline();

  const handleAddNote = () => {
    // Enter note placement mode
    setToolMode(TOOL_MODES.PLACE_NOTE);
  };

  const isPlacingNote = toolMode === TOOL_MODES.PLACE_NOTE;

  const handleZoomIn = () => {
    setZoom(Math.min(5, viewState.zoom * 1.2));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(0.1, viewState.zoom / 1.2));
  };

  const handleAddChapterBorder = () => {
    // Add border at center of current view with default width
    const centerX = -viewState.panX / viewState.zoom + 500;
    addBorder({ x: centerX, width: 400, label: 'New Chapter' });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: isDark ? 'rgba(50, 50, 50, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        borderRadius: 2,
        boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.15)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: 1,
        zIndex: 1000,
      }}
    >
      {/* Add Note */}
      <ToolButton
        icon={<AddIcon />}
        tooltip={isPlacingNote ? 'Click on canvas to place note (Esc to cancel)' : 'Add Note'}
        isActive={isPlacingNote}
        onClick={handleAddNote}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Cursor Mode */}
      <ToolButton
        icon={<CursorIcon />}
        tooltip="Cursor (Select/Move)"
        isActive={toolMode === TOOL_MODES.CURSOR}
        onClick={() => setToolMode(TOOL_MODES.CURSOR)}
      />

      {/* Pan Mode */}
      <ToolButton
        icon={<PanToolIcon />}
        tooltip="Pan Canvas"
        isActive={toolMode === TOOL_MODES.PAN}
        onClick={() => setToolMode(TOOL_MODES.PAN)}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Chapter Borders Toggle */}
      <ToolButton
        icon={<ChapterBorderIcon />}
        tooltip={showChapterBorders ? 'Hide Chapter Borders' : 'Show Chapter Borders'}
        isActive={showChapterBorders}
        onClick={toggleBordersVisibility}
      />

      {/* Add Chapter Border */}
      <ToolButton
        icon={<AddBorderIcon />}
        tooltip="Add Chapter Border"
        onClick={handleAddChapterBorder}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Zoom Out */}
      <ToolButton icon={<ZoomOutIcon />} tooltip="Zoom Out" onClick={handleZoomOut} />

      {/* Reset Zoom */}
      <ToolButton icon={<ResetZoomIcon />} tooltip="Reset Zoom & Pan" onClick={resetZoom} />

      {/* Zoom In */}
      <ToolButton icon={<ZoomInIcon />} tooltip="Zoom In" onClick={handleZoomIn} />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Undo */}
      <ToolButton icon={<UndoIcon />} tooltip="Undo" onClick={undo} disabled={!canUndo} />

      {/* Redo */}
      <ToolButton icon={<RedoIcon />} tooltip="Redo" onClick={redo} disabled={!canRedo} />
    </Box>
  );
};

export default TimelineToolbar;
